import { useState, useEffect, useRef } from "react";
import * as S from "./ComponentesGarantia.styles";
import DataGrid, {
  Column,
  HeaderFilter,
  Paging,
  Toolbar,
  Item,
  SearchPanel,
  FilterRow,
} from "devextreme-react/data-grid";
import { Popup } from "devextreme-react/popup";
import { Button } from "devextreme-react/button";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import notify from "devextreme/ui/notify";
import axios, { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { exportDataGrid } from "devextreme/excel_exporter";
import { ApiError } from "../../../services/apiError";
import { ComponenteGarantia } from "../../../dtos/ComponenteGarantia";
import { componentesGarantiaService } from "../../../services/componentesGarantiaService";
import ComponenteGarantiaFormPopup from "./ComponenteGarantiaFormPopup";

const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError>;
    return {
      message:
        error.response?.data?.message || "Ocurrió un error en el servidor.",
      status: error.response?.status,
      details: error.response?.data?.details || null,
    };
  }
  return {
    message: "Ocurrió un error inesperado. Revisa tu conexión de red.",
  };
};

const ComponentesGarantiaIndex = () => {
  const [dataSource, setDataSource] = useState<ComponenteGarantia[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormPopupVisible, setIsFormPopupVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<Partial<ComponenteGarantia> | null>(
    null,
  );
  const [isDeletePopupVisible, setIsDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<ComponenteGarantia | null>(null);
  const dataGridRef = useRef<any>(null);
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await componentesGarantiaService.getAll(false);
      setDataSource(response ?? []);
    } catch (err) {
      notify(handleApiError(err).message, "error", 3000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleExportExcel = async () => {
    const gridInstance = dataGridRef.current?.instance();
    if (!gridInstance) {
      notify("No se pudo obtener la tabla para exportar.", "error", 3000);
      return;
    }
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("ComponentesGarantia");
    await exportDataGrid({
      component: gridInstance,
      worksheet,
      autoFilterEnabled: true,
      customizeCell: ({ gridCell, excelCell }) => {
        if (gridCell?.rowType === "header") {
          excelCell.font = { bold: true };
        }
      },
    });
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      "ComponentesGarantia.xlsx",
    );
  };

  const handleAdd = () => {
    setEditingItem({ activo: true });
    setIsFormPopupVisible(true);
  };

  const handleEdit = (data: ComponenteGarantia) => {
    setEditingItem({ ...data });
    setIsFormPopupVisible(true);
  };

  const handleDelete = (data: ComponenteGarantia) => {
    setItemToDelete(data);
    setIsDeletePopupVisible(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      await componentesGarantiaService.remove(itemToDelete.id);
      setDataSource((prev) => prev.filter((x) => x.id !== itemToDelete.id));
      notify("Componente eliminado.", "success", 2000);
    } catch (err) {
      notify(handleApiError(err).message, "error", 3000);
    } finally {
      setIsDeletePopupVisible(false);
      setItemToDelete(null);
    }
  };

  const handleSaved = (item: ComponenteGarantia) => {
    setDataSource((prev) => {
      const idx = prev.findIndex((x) => x.id === item.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = item;
        return next;
      }
      return [...prev, item];
    });
  };

  const EstatusCell = (cellInfo: { value: boolean }) => {
    const isActive = cellInfo.value;
    return (
      <div
        style={{
          padding: "4px 10px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: 500,
          color: "#fff",
          backgroundColor: isActive ? "#16a34a" : "#71717a",
          textAlign: "center",
        }}
      >
        {isActive ? "Activo" : "Inactivo"}
      </div>
    );
  };

  const ActionsCell = (cellData: { data: ComponenteGarantia }) => {
    const item = cellData.data;
    return (
      <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
        <Button hint="Editar" onClick={() => handleEdit(item)} stylingMode="text">
          <FiEdit size={18} color="#1d4ed8" />
        </Button>
        <Button hint="Eliminar" onClick={() => handleDelete(item)} stylingMode="text">
          <FiTrash2 size={18} color="#dc2626" />
        </Button>
      </div>
    );
  };

  return (
    <S.PageWrapper>
      <S.GlobalPopupStyles />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "15px",
        }}
      >
        <Button
          icon="chevronleft"
          text="Regresar"
          stylingMode="outlined"
          type="default"
          onClick={() => navigate(-1)}
        />
      </div>
      <S.Header>Catálogo de componentes de garantía</S.Header>
      <DataGrid
        ref={dataGridRef}
        dataSource={dataSource}
        keyExpr="id"
        showBorders
        columnAutoWidth
        hoverStateEnabled
        loadPanel={{ enabled: loading }}
      >
        <FilterRow visible />
        <HeaderFilter visible allowSearch />
        <Paging defaultPageSize={50} />
        <Toolbar>
          <Item name="searchPanel" />
          <Item
            location="after"
            widget="dxButton"
            options={{
              text: "Exportar Excel",
              icon: "xlsxfile",
              stylingMode: "contained",
              type: "default",
              onClick: handleExportExcel,
            }}
          />
          <Item
            location="after"
            widget="dxButton"
            options={{
              text: "Nuevo componente",
              icon: "add",
              stylingMode: "contained",
              type: "default",
              onClick: handleAdd,
            }}
          />
        </Toolbar>
        <SearchPanel visible highlightCaseSensitive placeholder="Buscar..." />
        <Column dataField="codigo" caption="Código" width={120} />
        <Column dataField="nombre" caption="Nombre" width={280} />
        <Column
          dataField="activo"
          caption="Estatus"
          alignment="center"
          cellRender={EstatusCell}
          width={120}
        />
        <Column
          caption="Acciones"
          alignment="center"
          width={140}
          cellRender={ActionsCell}
          allowExporting={false}
        />
      </DataGrid>

      <ComponenteGarantiaFormPopup
        visible={isFormPopupVisible}
        editingItem={editingItem}
        onHiding={() => setIsFormPopupVisible(false)}
        onSaved={handleSaved}
      />

      <Popup
        visible={isDeletePopupVisible}
        onHiding={() => setIsDeletePopupVisible(false)}
        dragEnabled={false}
        showTitle
        title="Confirmar eliminación"
        width={420}
        height="auto"
        className="custom-form-popup"
      >
        <div style={{ padding: "20px", textAlign: "center" }}>
          <p>
            ¿Eliminar el componente{" "}
            <strong>{itemToDelete?.codigo}</strong>
            {itemToDelete?.nombre ? ` (${itemToDelete.nombre})` : ""}?
          </p>
        </div>
        <S.PopupFooter>
          <S.CancelButton onClick={() => setIsDeletePopupVisible(false)}>
            Cancelar
          </S.CancelButton>
          <S.SaveButton onClick={confirmDelete}>Eliminar</S.SaveButton>
        </S.PopupFooter>
      </Popup>
    </S.PageWrapper>
  );
};

export default ComponentesGarantiaIndex;
